import { type ComponentType, type FC, type ReactNode, useCallback } from 'react';
import {
    Button,
    Card,
    type ColorlessIconProps,
    Header,
    type IconBaseProps,
    type TestProps,
    VStack,
} from '@direct-frontend/components';
import { StatefulTooltip } from '#shared/ui/StatefulTooltip';

interface SiteVerificationCardProps extends TestProps {
    Icon: ComponentType<IconBaseProps>;
    details?: ReactNode;
    guardReason?: string;
    isConnected: boolean;
    isConnecting?: boolean;
    lead: string;
    onConnect: VoidFunction;
    title: string;
}

export const SiteVerificationCard: FC<SiteVerificationCardProps> = ({
    Icon,
    details,
    guardReason,
    isConnected,
    isConnecting = false,
    lead,
    onConnect,
    title,
    'data-testid': dataTestId = 'SiteVerificationCard',
}) => {
    const isDisabled = isConnected || Boolean(guardReason);
    const ButtonIcon = useCallback((props: ColorlessIconProps) => <Icon {...props} />, [Icon]);
    const button = (
        <Button
            color="contour"
            data-testid={`${dataTestId}.Button`}
            iconLeft={ButtonIcon}
            isDisabled={isDisabled}
            isLoading={isConnecting}
            onClick={onConnect}
            size="m"
            width="max"
        >
            {isConnected ? 'Подключено' : 'Подключить'}
        </Button>
    );

    return (
        <Card color="gray" borderRadius="14" padding="20" data-testid={dataTestId}>
            <VStack gap="12">
                <VStack gap="20">
                    <Header level="h4" caption={lead}>
                        {title}
                    </Header>
                    {guardReason && !isConnected ? (
                        <StatefulTooltip title={guardReason} maxWidth="360">
                            <div data-testid={`${dataTestId}.GuardTrigger`}>{button}</div>
                        </StatefulTooltip>
                    ) : (
                        button
                    )}
                </VStack>
                {details}
            </VStack>
        </Card>
    );
};
